import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { CmpReportTypeEnum, useExportCmpReportCsvLazyQuery } from "../../../../graphql/types";
import useGetUserFromUserIdInAuth from "../../../../hooks/useGetUserFromUserIdInAuth";
import ReportPreviewModal, { type ReportPreviewState } from "../../Reports/ReportPreviewModal";
import { parseReportCsv } from "../../Reports/parseReportCsv";
import { formatReportPeriod } from "../../Reports/reportDateRanges";
import { printEventAttendeePreview } from "../../Reports/eventAttendeePrint";
import { printInvoicePreviewDocument } from "../../Invoicing/ManageInvoices/ManageInvoiceActionModals";
import { downloadTextFile } from "../../Invoicing/invoiceFileDownloads";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../utils/apolloErrorPolicy";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../../utils/userFacingError";

export type EventReportSheetEvent = {
  id: string;
  title: string;
  publishAtUtcMilli?: number | null;
  createdAtUtcMilli?: number | null;
  /** Close Event Registration time: the period ends here (ME:522). */
  registrationClosedAtUtcMilli?: number | null;
  timeZone?: string | null;
};

const reportQueryErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: [] });
const EVENT_REPORT_TAIL =
  " Submitted Question appears only for events where attendees were invited to submit a question for the speaker or panel. Complimentary tickets are sponsor entitlements and carry no ticket price. Table purchases are listed as one ticket per seat with the attendee name supplied by the member; seats still to be named show as pending.";

/**
 * Manage Events View/Export Attendee Report and View/Export Sponsor Report
 * (IF11 EV12, Manage Events Screen (21) :185-199): the Reports sheet for one
 * event. The period line runs from the on-sale date (publish date, or the
 * created date when there is none) to today; it labels the sheet and does not
 * remove rows (IF11 Q20). Filters read "Event Name: <event>".
 */
const EventReportSheet = ({
  chamberId,
  event,
  kind,
  onClose,
}: {
  chamberId: string;
  event: EventReportSheetEvent;
  kind: "attendees" | "sponsors";
  onClose: () => void;
}) => {
  const user = useGetUserFromUserIdInAuth();
  const chamber = user?.chamberInfo;
  const reportType = kind === "attendees" ? CmpReportTypeEnum.EventAttendeeList : CmpReportTypeEnum.EventSponsorshipList;
  const reportTitle = kind === "attendees" ? "Event Attendee List" : "Event Sponsorship Report";
  const reportInput = { chamberId, reportType, eventIds: [event.id] };
  const [state, setState] = useState<ReportPreviewState>({ status: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(null);
  const [exportReport] = useExportCmpReportCsvLazyQuery({ fetchPolicy: "no-cache", context: reportQueryErrorContext });
  const requested = useRef(false);
  const zone = event.timeZone || "America/Toronto";
  const onSaleUtcMilli = event.publishAtUtcMilli || event.createdAtUtcMilli || null;
  const startDate = onSaleUtcMilli ? DateTime.fromMillis(onSaleUtcMilli, { zone }).toISODate() || "" : "";
  const endDate = (event.registrationClosedAtUtcMilli
    ? DateTime.fromMillis(event.registrationClosedAtUtcMilli, { zone })
    : DateTime.now().setZone(zone)).toISODate() || "";
  const filters = [{ label: "Event Name", value: event.title }];

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    exportReport({ variables: { input: reportInput } })
      .then((result) => {
        if (result.error) throw result.error;
        const exported = result.data?.exportCmpReportCsv;
        if (!exported) throw new Error("The report export did not return a CSV file.");
        setState({
          status: "ready",
          ...parseReportCsv(exported.csvContent),
          rowCount: exported.rowCount,
          generatedAtUtcMilli: exported.generatedAtUtcMilli,
          filename: exported.filename,
          document: exported.document,
          csvContent: exported.csvContent,
        });
      })
      .catch((error) => setState({ status: "error", message: getUserFacingErrorText(error, buildStaffErrorFallback("load the report preview")) }));
    // The report loads once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const print = () => {
    if (kind !== "attendees" || state.status !== "ready") {
      printInvoicePreviewDocument();
      return;
    }
    const opened = printEventAttendeePreview({
      eventLabel: event.title,
      filters,
      periodLabel: startDate ? formatReportPeriod({ startDate, endDate }) : "",
      preview: state,
      timeZone: user?.chamberMemberProTimeZone,
    });
    if (!opened) setActionError("The print window was blocked. Allow pop-ups for this site, then click Print again.");
  };

  const download = () => {
    if (state.status !== "ready" || !state.csvContent) return;
    downloadTextFile({ content: state.csvContent, contentType: "text/csv", filename: state.filename });
    setDownloaded(state.filename);
  };

  return (
    <>
      <ReportPreviewModal
        open
        onClose={onClose}
        state={state}
        reportTitle={reportTitle}
        chamber={chamber}
        startDate={startDate}
        endDate={endDate}
        timeZone={user?.chamberMemberProTimeZone}
        onPrint={print}
        onDownload={download}
        chamberId={chamberId}
        reportInput={reportInput}
        filterSummary={filters.map(({ label, value }) => `${label}: ${value}`).join(", ")}
        actionError={actionError}
        onActionErrorClose={() => setActionError(null)}
        backLabel="Back"
        footerText={kind === "attendees"
          ? `Attendees holding a ticket to ${event.title}, from the date tickets went on sale to the date this report was generated or event registration was closed.${EVENT_REPORT_TAIL}`
          // IF11 audit L4: one event's sheet names the event, not "across all events".
          : `Sponsorships confirmed for ${event.title}. Tickets are the complimentary seats included in each level.`}
      />
      <ActionSuccessDialog
        message={downloaded ? `A .csv file (${downloaded}) has been downloaded!` : null}
        highlights={downloaded ? [downloaded] : undefined}
        onClose={() => setDownloaded(null)}
      />
    </>
  );
};

export default EventReportSheet;
